import { useCallback, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';

import { isSatisfyOption, sendSentryClientError } from 'shared/lib';

import {
  setIsSmsSending,
  setLastConfirmationResult,
  useConfirmationRequestDeliveryMutation,
  useIsSmsSending,
  useLastConfirmationResult,
} from 'entities/preApprovals';

import { MAX_RETRY_TIME_IN_SECONDS } from 'features/PreApprovalConfirmationForm/PreApprovalConfirmationForm.config';

import { CONFIRMATION_STATUS_CODES } from './constants';
import { useApplyTimerFromConfirmationResponse } from './useApplyTimerFromConfirmationResponse';
import { useCountdownTimer } from './useCountdownTimer';

export const useSendSmsConfirmation = (preapprovalOfferId: string) => {
  const isSmsSending = useIsSmsSending();
  const lastResult = useLastConfirmationResult();
  const dispatch = useDispatch();
  const applyTimerFromResponse = useApplyTimerFromConfirmationResponse();
  const [confirmationRequestDelivery] = useConfirmationRequestDeliveryMutation();
  const { secondsLeft, retryTimerText, startTimer, resetTimer } = useCountdownTimer();

  useEffect(() => {
    startTimer();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const sendSmsAgain = useCallback(async () => {
    dispatch(setIsSmsSending(true));

    try {
      resetTimer();
      const res = await confirmationRequestDelivery({ offerId: preapprovalOfferId }).unwrap();
      dispatch(setLastConfirmationResult(res));
      applyTimerFromResponse(res);
      if (res.retryAfterInSeconds && res.retryAfterInSeconds < MAX_RETRY_TIME_IN_SECONDS) {
        startTimer();
      }
    } catch (e) {
      sendSentryClientError(e as Error);
    } finally {
      dispatch(setIsSmsSending(false));
    }
  }, [dispatch, resetTimer, confirmationRequestDelivery, preapprovalOfferId, applyTimerFromResponse, startTimer]);

  return useMemo(
    () => ({
      isSmsSending,
      statusCode: lastResult?.statusCode,
      isSmsSuccess: isSatisfyOption(
        [CONFIRMATION_STATUS_CODES.SUCCESS, CONFIRMATION_STATUS_CODES.CREATED],
        lastResult?.statusCode,
      ),
      retryTimerText,
      secondsLeft,
      sendSmsAgain,
      isTooLongRetryTime: secondsLeft > MAX_RETRY_TIME_IN_SECONDS,
    }),
    [sendSmsAgain, isSmsSending, secondsLeft, lastResult?.statusCode, retryTimerText],
  );
};
